import React, { useEffect, useState, useCallback } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import classnames from 'classnames/bind';
import {
    Split,
    RotateCcw,
    CheckCircle2,
    Tag,
    Paperclip,
} from 'lucide-react';

import { notify } from '@NOTIFICATION/Notificator';
import {
    useUpdateSection,
    useDeleteTagFromSection,
    useAddTagToSection,
    useCreateAndAddTagToSection,
    useGetAllTags,
    attachmentsAdminApi,
} from '@SERVICES';
import { AdminSingleSectionData, AdminSingleSectionMeta } from '@INTERFACES/admin-api/sections';
import SectionName from '@PAGES/admin/edit-section/components/SectionName';
import TagsComponent from '@COMPONENTS/COMMON/tags/tags.component';
import useDocumentPermissions from '@HOOKS/useDocumentPermissions';

import BasicCheckbox from '@COMPONENTS/COMMON/inputs/checkbox/BasicCheckbox';
import useAttachmentManagementModal from '@HOOKS/store/modals/useAttachmentManagementModal';
// import usePublishSectionBaseTextModal from '@HOOKS/store/modals/usePublishSectionBaseTextModal';
import styles from './PageHeader.module.scss';
import { SaveOption } from './types';

const cx: CX = classnames.bind(styles);

function PageHeader({
    sectionData,
    sectionMeta,
    actionButton,
    bodyCotent,
}: Props) {
    const [updateSection, { isSuccess, isError, error }] = useUpdateSection();
    const [isPageBreak, setPageBreak] = useState<boolean>(sectionData.isPageBreak);
    const [isLandscape, setLandscape] = useState<boolean>(sectionData.isLandscape);
    const [showTags, setShowTags] = useState<boolean>(false);

    const {
        canManageDocumentTags,
        canEditDocument,
    } = useDocumentPermissions({
        documentOwnerId: sectionData.document.owner,
        permissions: sectionMeta.permissions,
    });

    const {
        actions: attachmentManagementModalActions,
    } = useAttachmentManagementModal(() => true);

    const { data: attachmentsData } = attachmentsAdminApi.useGetAttachmentsQuery({
        content_type: 'section',
        object_id: sectionData.id,
    });
    const attachments = attachmentsData || [];

    const [deleteTag, deleteTagResult] = useDeleteTagFromSection();
    const [addTagToSection, addTagToSectionResult] = useAddTagToSection();
    const [createAndAddTagToVariant, createAndAddTagToVariantResult] = useCreateAndAddTagToSection();

    // const {
    //     actions: publishSectionBaseTextModalActions,
    // } = usePublishSectionBaseTextModal();

    useEffect(() => {
        if (isSuccess) {
            notify.info('Section has been updated', { toastId: 'update-section-params' });
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            const errorMessage = (error as FetchBaseQueryError).status === 403
                ? 'Permission denied'
                : 'Something went wrong!';
            notify.error(errorMessage, { toastId: 'update-section-params-error' });
        }
    }, [isError, error]);

    const handleSave = useCallback((option: SaveOption) => {
        updateSection({
            id: sectionData.id,
            chapterId: sectionData.chapter.id,
            documentId: sectionData.document.id,
            name: sectionData.name,
            body: sectionData.baseText.body,
            isMandatory: sectionData.isMandatory,
            isPageBreak,
            isLandscape,
            ...option,
        });
    }, [sectionData, isPageBreak, isLandscape, updateSection]);

    const toggleOption = useCallback((optionName: 'isPageBreak' | 'isLandscape') => {
        const setter = optionName === 'isPageBreak' ? setPageBreak : setLandscape;
        setter((prevValue) => {
            const newValue = !prevValue;
            handleSave({ [optionName]: newValue });
            return newValue;
        });
    }, [handleSave]);

    const tagCount = sectionData.tags?.length || 0;

    return (
        <div className={cx('page-header')}>
            <div className={cx('page-wrap')}>
                {/* Main header section with section name and primary actions */}
                <div className={cx('header-main')}>
                    <div className={cx('section-info')}>
                        <SectionName
                            sectionData={sectionData}
                            sectionMeta={sectionMeta}
                            actionButton={actionButton}
                            bodyCotent={null}
                        />
                    </div>
                </div>

                {/* Compact tags section - TEMPORARILY HIDDEN */}
                {/* <div className={cx('tags-section')}>
                    <div
                        className={cx('tags-header')}
                        onClick={() => setShowTags(!showTags)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setShowTags(!showTags);
                            }
                        }}
                    >
                        <div className={cx('tags-info')}>
                            <Tag size={16} className={cx('tags-icon')} />
                            <span className={cx('tags-label')}>Tags</span>
                            <span className={cx('tags-count')}>
                                (
                                {tagCount}
                                )
                            </span>
                        </div>
                        <div className={cx('tags-toggle', { expanded: showTags })}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6,9 12,15 18,9" />
                            </svg>
                        </div>
                    </div>
                    {showTags && (
                        <div className={cx('tags-wrapper')}>
                            <TagsComponent
                                locked={!canManageDocumentTags}
                                tags={sectionData.tags}
                                tagQuery={[deleteTag, deleteTagResult]}
                                apiParams={{
                                    chapterId: sectionData.chapter.id,
                                    id: sectionData.id,
                                    documentId: sectionData.document.id,
                                }}
                                getAllTagsQuery={useGetAllTags}
                                getTagsParams={{
                                    documentId: sectionData.document.id,
                                }}
                                addTagQuery={[addTagToSection, addTagToSectionResult]}
                                addTagParams={{
                                    id: sectionData.id,
                                    chapterId: sectionData.chapter.id,
                                }}
                                createAndAddTagQuery={[createAndAddTagToVariant, createAndAddTagToVariantResult]}
                                createAndAddTagParams={{
                                    documentId: sectionData.document.id,
                                    chapterId: sectionData.chapter.id,
                                    id: sectionData.id,
                                }}
                                sectionData={sectionData}
                            />
                            {bodyCotent}
                        </div>
                    )}
                </div> */}

                {/* Options section with clickable cards */}
                <div className={cx('options-section')}>
                    <div className={cx('options-header')}>
                        <h3 className={cx('options-title')}>Section Options</h3>
                        <div className={cx('options-divider')} />
                    </div>
                    
                    <div className={cx('options-grid')}>
                        {/* Page Break Option Card */}
                        <div 
                            className={cx('option-card', { active: isPageBreak })}
                            onClick={() => toggleOption('isPageBreak')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    toggleOption('isPageBreak');
                                }
                            }}
                        >
                            <div className={cx('option-content')}>
                                <div className={cx('option-icon')}>
                                    <Split size={20} />
                                </div>
                                <div className={cx('option-info')}>
                                    <div className={cx('option-title')}>Page Break</div>
                                    <div className={cx('option-description')}>
                                        Add a page break before this section starts
                                    </div>
                                </div>
                                <div className={cx('option-status', { active: isPageBreak })}>
                                    {isPageBreak && <CheckCircle2 size={18} />}
                                </div>
                            </div>
                        </div>

                        {/* Landscape Mode Option Card */}
                        <div 
                            className={cx('option-card', { active: isLandscape })}
                            onClick={() => toggleOption('isLandscape')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    toggleOption('isLandscape');
                                }
                            }}
                        >
                            <div className={cx('option-content')}>
                                <div className={cx('option-icon')}>
                                    <RotateCcw size={20} />
                                </div>
                                <div className={cx('option-info')}>
                                    <div className={cx('option-title')}>Landscape Mode</div>
                                    <div className={cx('option-description')}>
                                        Render this section in landscape orientation
                                    </div>
                                </div>
                                <div className={cx('option-status', { active: isLandscape })}>
                                    {isLandscape && <CheckCircle2 size={18} />}
                                </div>
                            </div>
                        </div>

                        {/* Attachments Option Card */}
                        {canEditDocument && (
                            <div 
                                className={cx('option-card')}
                                onClick={() => {
                                    attachmentManagementModalActions.open({
                                        parentType: 'section',
                                        parentId: sectionData.id,
                                        parentName: sectionData.name,
                                        canEdit: canEditDocument,
                                    });
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        attachmentManagementModalActions.open({
                                            parentType: 'section',
                                            parentId: sectionData.id,
                                            parentName: sectionData.name,
                                            canEdit: canEditDocument,
                                        });
                                    }
                                }}
                            >
                                <div className={cx('option-content')}>
                                    <div className={cx('option-icon')}>
                                        <Paperclip size={20} />
                                    </div>
                                    <div className={cx('option-info')}>
                                        <div className={cx('option-title')}>
                                            Manage Attachments
                                            {attachments.length > 0 && (
                                                <span className={cx('attachment-count')}>
                                                    {' '}({attachments.length})
                                                </span>
                                            )}
                                        </div>
                                        <div className={cx('option-description')}>
                                            Upload and manage PDF attachments for this section
                                        </div>
                                    </div>
                                    <div className={cx('option-status')}>
                                        {attachments.length > 0 && <CheckCircle2 size={18} />}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

interface Props {
    sectionData: AdminSingleSectionData;
    sectionMeta: AdminSingleSectionMeta;
    actionButton?: React.ReactNode;
    bodyCotent?: React.ReactNode;
}

PageHeader.defaultProps = {
    actionButton: null,
    bodyCotent: null,
};

export default PageHeader;
